import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Tooltip,
} from '@material-ui/core';
import { nanoid } from '@reduxjs/toolkit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useFormContext } from 'react-hook-form';
import { useField } from 'hooks/useField.hook';
import { generateTheme } from '../../../utils/helpers';
import {
  HISTORICAL_CONDITIONS,
  MEDICAL_CONDITIONS,
} from '../medical-conditions/medical-conditions-table.utils';

const useStyles = makeStyles(theme => ({
  head: {
    backgroundColor: '#293241',
  },
  cellHeader: {
    fontFamily: theme.typography.h1.fontFamily,
    fontWeight: theme.typography.h1.fontWeight,
    color: 'white',
  },
  row: {
    '&:nth-child(odd)': {
      backgroundColor: '#e3e3e3',
    },
    '&:nth-child(even)': {
      backgroundColor: '#a9a9a9',
    },
  },
}));

export const SUPPLEMENTS = [
  'Multivitamínico',
  'Proteína en polvo',
  'Omega 3',
  'Magnesio',
  'Probióticos',
  'Otro',
].toSorted((a, b) => a.localeCompare(b));

export const SupplementsTable = () => {
  const [isFormOpen, toggleForm] = useState(false);

  const nameField = useField('');

  const doseField = useField('');

  const reasonField = useField('');

  const noteField = useField('');

  const form = useFormContext();

  const classes = useStyles();

  const supplements = form.watch('supplements');

  const onDeleteSupplementHandler = item => {
    const _supplement = supplements.filter(
      supplement => supplement._id !== item._id,
    );
    form.setValue('supplements', _supplement);
  };

  const onOpenFormHandler = () => {
    toggleForm(prevState => !prevState);
  };

  const onSubmitHandler = () => {
    const name = nameField.value;
    const dose = doseField.value;
    const reason = reasonField.value;
    const note = noteField.value;

    const nameValidation = nameField.validate();
    const doseValidation = doseField.validate();
    const reasonValidation = reasonField.validate();
    const noteValidation = noteField.validate();

    if (
      nameValidation ||
      noteValidation ||
      doseValidation ||
      reasonValidation
    ) {
      const payload = {
        name,
        dose,
        reason,
        note,
        _id: nanoid(),
      };
      const _supplements = [...supplements, payload];
      form.setValue('supplements', _supplements);
      toggleForm(false);
      nameField.reset();
      doseField.reset();
      reasonField.reset();
      noteField.reset();
    }
  };

  return (
    <ThemeProvider theme={theme => generateTheme(theme, 'light')}>
      <Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead className={classes.head}>
              <TableRow>
                <TableCell align="center" className={classes.cellHeader}>
                  Nombre
                </TableCell>
                <TableCell align="center" className={classes.cellHeader}>
                  Dosis
                </TableCell>
                <TableCell align="center" className={classes.cellHeader}>
                  Razón
                </TableCell>
                <TableCell align="center" className={classes.cellHeader}>
                  Nota
                </TableCell>
                <TableCell align="center" className={classes.cellHeader}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supplements.map(supplement => (
                <TableRow key={nanoid()} className={classes.row}>
                  <TableCell align="center">{supplement.name}</TableCell>
                  <TableCell align="center">{supplement.dose}</TableCell>
                  <TableCell align="center">{supplement.reason}</TableCell>
                  <TableCell align="center">{supplement.note}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Eliminar">
                      <IconButton
                        onClick={() => {
                          onDeleteSupplementHandler(supplement);
                        }}
                      >
                        <DeleteIcon color="secondary" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onOpenFormHandler}
          >
            Agregar nuevo suplemento
          </Button>
        </Box>
        {isFormOpen && (
          <Box mt={2}>
            <Grid container spacing={4} style={{ marginBottom: '16px' }}>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="supplementLabel">Nombre</InputLabel>
                  <Select
                    fullWidth
                    labelId="Nombre"
                    color="secondary"
                    error={nameField.error}
                    value={nameField.value}
                    onChange={nameField.onChange}
                  >
                    {SUPPLEMENTS.map(supplement => (
                      <MenuItem key={nanoid()} value={supplement}>
                        {supplement}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Dosis"
                  color="secondary"
                  helperText={
                    doseField.error ? 'Este campo es requerido' : 'Dosis'
                  }
                  error={doseField.error}
                  value={doseField.value}
                  onChange={doseField.onChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Razón"
                  color="secondary"
                  helperText={
                    reasonField.error ? 'Este campo es requerido' : 'Razón'
                  }
                  error={reasonField.error}
                  value={reasonField.value}
                  onChange={reasonField.onChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nota"
                  color="secondary"
                  helperText={
                    noteField.error ? 'Este campo es requerido' : 'Notas'
                  }
                  error={noteField.error}
                  value={noteField.value}
                  onChange={noteField.onChange}
                />
              </Grid>
            </Grid>
            <Box textAlign="right">
              <Button
                variant="contained"
                color="secondary"
                onClick={onSubmitHandler}
              >
                Agregar
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};
